import React, { createContext } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranscript } from '@utils/providers/transcript-context.tsx';
import { ReactEditor } from 'slate-react';
import { useGetUserPreferences } from '@utils/api/organizations/user-preferences/use-get-user-preferences.tsx';
import { KEYMAP_INPUT_KEY } from '@utils/constants/app-constants.ts';
import { KeyboardKeymapInput } from '@utils/types/user-preferences/user-preferences.ts';
import { Transforms } from 'slate';
import { formatKeymap } from '@utils/editors/keymap-parsing.ts';

interface TranscriptEditorLevelHotkeyContextType {}

const TranscriptEditorLevelHotkeyContext = createContext<
  TranscriptEditorLevelHotkeyContextType | undefined
>(undefined);

interface TranscriptEditorLevelHotkeyProviderProps {
  children: React.ReactNode;
  editor: ReactEditor;
}

export function TranscriptEditorLevelHotkeyProvider({
  children,
  editor
}: TranscriptEditorLevelHotkeyProviderProps) {
  const { document, startStopWord, applyStyle, currentEditorRef } =
    useTranscript();
  const { data: preferences } = useGetUserPreferences();

  const customKeymap = preferences?.result
    ?.filter(preferenceKey => preferenceKey.key === KEYMAP_INPUT_KEY)
    ?.map(preference => {
      try {
        return JSON.parse(preference.value) as KeyboardKeymapInput;
      } catch (e) {
        return null;
      }
    })
    .filter(Boolean)[0];

  const insertText = (text: string) => {
    if (!editor.selection) return;
    Transforms.insertText(editor, text);
  };

  const formattedKeymap = customKeymap ? formatKeymap(customKeymap.key) : '';

  useHotkeys(
    formattedKeymap,
    e => {
      if (
        customKeymap &&
        currentEditorRef &&
        currentEditorRef.current === editor
      ) {
        e.preventDefault();
        insertText(customKeymap.value);
      }
    },
    {
      enableOnFormTags: true,
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: !!customKeymap
    },
    [editor, customKeymap, currentEditorRef]
  );

  // ctrl+b apply bold style
  useHotkeys(
    ['ctrl+b', 'meta+b'],
    () => {
      applyStyle('bold', null, editor);
    },
    {
      enableOnFormTags: true,
      preventDefault: true,
      enableOnContentEditable: true
    },
    [document, editor, startStopWord]
  );

  // ctrl+i apply italic style
  useHotkeys(
    ['ctrl+i', 'meta+i'],
    () => {
      applyStyle('italic', null, editor);
    },
    {
      enableOnFormTags: true,
      preventDefault: true,
      enableOnContentEditable: true
    },
    [document, editor, startStopWord]
  );

  // ctrl+u apply underline style
  useHotkeys(
    ['ctrl+u', 'meta+u'],
    () => {
      applyStyle('underlined', null, editor);
    },
    {
      enableOnFormTags: true,
      preventDefault: true,
      enableOnContentEditable: true
    },
    [document, editor, startStopWord]
  );

  // ctrl+s apply strike style
  useHotkeys(
    ['ctrl+s', 'meta+s'],
    () => {
      applyStyle('strike', null, editor);
    },
    {
      enableOnFormTags: true,
      preventDefault: true,
      enableOnContentEditable: true
    },
    [document, editor, startStopWord]
  );

  return (
    <TranscriptEditorLevelHotkeyContext.Provider
      value={{
        editor
      }}
    >
      {children}
    </TranscriptEditorLevelHotkeyContext.Provider>
  );
}
